<template>
    <form
        ref="formRef" 
        class="comment-input"
        :class="{ 'comment-input--has-value': inputValue.length > 0 }"
        @submit.prevent="submit"
    >
        <BaseAvatar class="comment-input__avatar" use-user-avatar />
        <label for="comment-input" class="sr-only">Leave a comment</label>
        <BaseTextarea
            id="comment-input"
            ref="textareaRef"
            v-model="inputValue"
            :rows="1"
            :wrapper-attrs="{ class: 'comment-input__input'}"
            placeholder="Leave a comment"
            @keydown.enter.exact.prevent="submit"
        />
        <BaseButton
            type="submit"
            class="base-button--icon-only comment-input__submit"
            icon-left="send"
        />
    </form>
</template>

<script setup>
import { useCommentStore } from '~/store/comment';
import CreateCommentMutation from '~/graphql/mutations/createComment.graphql';
import { useFocusWithin } from '@vueuse/core';

const commentStore = useCommentStore();

const { push } = useTagmanager();

const props = defineProps({
    focusWhenVisible: Boolean,

    commentId: {
        type: String,
        default: null
    },

    videoId: {
        type: String,
        required: true
    }
});


const { execute: createComment } = useAsyncMutation(`create-comment-${props.videoId}` + (props.commentId ? `-${props.commentId}` : ''));

const formRef = ref(null);
const inputValue = ref('');
const textareaInput = ref(null);

const emit = defineEmits(['blur']);

const { focused } = useFocusWithin(formRef);

onMounted(() => {
    textareaInput.value = formRef.value.querySelector('textarea');

    if (props.focusWhenVisible) {
        textareaInput.value.focus();
    }
});

watch(focused, (value) => {
    if (!value) {
        emit('blur');
    }
});

async function submit() {
    if (!inputValue.value.replace(/\s/g, '').length) {
        return;
    }

    textareaInput.value.blur();

    const { error } = await createComment({
        mutation: CreateCommentMutation,
        variables: {
            text: inputValue.value,
            videoId: props.videoId,
            commentId: props.commentId,
        }
    });

    if (!error.value) {
        inputValue.value = '';

        push({
            event: 'commented'
        });

        setTimeout(() => { // reset input height
            textareaInput.value.style.height = 'inherit';
        }, 100);

        commentStore.fetchComments(props.videoId);
    }
}
</script>

<style lang="less">
.comment-input {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: start;
    margin-bottom: var(--spacing-sm);

    .dn-textarea__field-body {
        padding: var(--spacing-xs);
    }

    .base-avatar {
        margin-right: var(--spacing-xs);
        height: 2rem;
    }

    &.comment-input--has-value {
        .comment-input__submit {
            margin-left: var(--spacing-xs);
            transform: scale(1);
            max-width: var(--size);
            max-height: var(--size);
        }
    }
}

.dn-textarea.comment-input__input {
    .typography-body-md();
    line-height: 1.4;
    border-radius: var(--borderRadius-md);
    border-top-left-radius: 0;
    border: 0;

    textarea {
        &::placeholder {
            color: var(--color-secondary);
        }
    }
}

.base-button.comment-input__submit {
    --size: 2.5rem;

    align-self: end;
    max-width: 0;
    max-height: 0;
    transform: scale(0);
    transition: transform 0.1s, max-width 0.1s;

    @media @q-md-min {
        --size: 2.625rem;
    }
}
</style>~/store/comment